import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { Avatar, Box, Button, Stack, Toolbar, Typography } from "@mui/material";
import { getUserInitials, useGraphService } from "../../Services/GraphService";
import logo from "./assets/logo.png";
import { DetailedAvailabilityTab } from "./DetailedAvailabilityTab";
import { MFTEnvironmentVariables } from "../../MFTEnvironmentVariables";
import { Divider } from "./Divider";
import { useThemeMode } from "../../theme/atom";
import { ThemeSwitch } from "./themeSwitch";
import { StartWithoutExchangeMenu } from "./StartWithoutExchangeMenu";
import { NAV_BAR_HEIGHT, NAV_LOGO_HEIGHT } from "./navBarMetrics";

import GitInfo from "../../generatedGitInfo.json";

function stringToColor(string: string) {
  let hash = 0;
  let i: number;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.substr(-2);
  }

  return color;
}

function stringAvatar(name: string) {
  return {
    sx: {
      bgcolor: stringToColor(name),
      width: 35,
      height: 35,
    },
    children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
  };
}

const NavBar = () => {
  const { instance } = useMsal();
  const graphService = useGraphService();

  const isProduction: boolean = MFTEnvironmentVariables.isProduction;

  const avatarStyling = { width: 30, height: 30, verticalAlign: "center" };

  const [themeMode, setThemeMode] = useThemeMode();

  return (
    <Toolbar
      variant="dense"
      sx={{
        justifyContent: "space-between",
        height: NAV_BAR_HEIGHT,
        bgcolor: (theme) =>
          isProduction
            ? theme.palette.chrome.navProd
            : theme.palette.chrome.navTest,
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img
            style={{
              // Definite pixels, not a percentage: nothing up this flex chain
              // has a resolvable height, so `height: x%` collapses to the
              // image's intrinsic size and overflows the bar.
              height: NAV_LOGO_HEIGHT,
            }}
            src={logo}
            alt="JEPX Lazer Desk"
          ></img>
        </Box>
        <DetailedAvailabilityTab />
      </Stack>
      {!isProduction && <Typography fontSize={16}>TEST</Typography>}
      <AuthenticatedTemplate>
        <Stack alignItems="center" direction="row" height="35px" spacing={2}>
          <Box sx={{ display: "flex", alignItems: "center", height: 25 }}>
            {graphService.user !== undefined &&
              (graphService.photo !== undefined ? (
                <Avatar
                  alt="test"
                  src={URL.createObjectURL(graphService.photo)}
                  sx={avatarStyling}
                />
              ) : (
                <Avatar
                  {...stringAvatar(String(graphService.user.displayName))}
                  sx={avatarStyling}
                />
              ))}
          </Box>
          <Typography variant="subtitle1" fontSize={14} color="white">
            {`${graphService.user?.givenName} (${getUserInitials(
              graphService.user
            ).toUpperCase()})`}
          </Typography>
          <Divider />
          {!isProduction && (
            <>
              <StartWithoutExchangeMenu />
              <Divider />
            </>
          )}
          <ThemeSwitch
            checked={themeMode === "kawaii"}
            inputProps={{ "aria-label": "Toggle kawaii theme" }}
            onChange={() =>
              setThemeMode(themeMode === "kawaii" ? "tokyoNight" : "kawaii")
            }
          />
          <Divider />
          <Button
            sx={{ fontSize: "12px" }}
            onClick={() => instance.logoutRedirect()}
          >
            LOGOUT
          </Button>
          <Divider />
          <Box>
            <Typography sx={{ fontSize: "10px" }}>
              {GitInfo ? "commit: " + GitInfo?.gitCommitHash : ""}
            </Typography>
          </Box>
        </Stack>
      </AuthenticatedTemplate>
    </Toolbar>
  );
};

export { NavBar };
